import React, { useMemo } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  HighlightType,
  HighlightVariant,
  Icon,
  type DataTableCellData,
} from '@node-space/ui-components'

export interface EarnMyAsset {
  currency: string
  productName?: string
  myEarnRate?: string
  customerEarnRate?: string
  availableFundsAmount?: string
  availableFundsLabel?: string
  comingSoon?: boolean
  onWithdrawClick?: () => void
  onDepositClick?: () => void
}

export interface EarnMyAssetsProps {
  assets: EarnMyAsset[]
  showCustomerEarnRate?: boolean
}

const ASSET_AVATAR_CURRENCY_OVERRIDES: Record<string, string> = {
  OUSD: 'USDC',
}

const getAssetAvatarCurrency = (currency: string) =>
  ASSET_AVATAR_CURRENCY_OVERRIDES[currency] ?? currency

const buildAssetActionsMenu = (asset: EarnMyAsset, t: TFunction<'translation', undefined>) => {
  const menuItems = [
    asset.onWithdrawClick && (
      <DropdownMenuItem
        key="withdraw"
        LeadingIcon={<Icon name="CoinArrowOutCustom" />}
        onClick={asset.onWithdrawClick}
      >
        {t('earn.withdraw')}
      </DropdownMenuItem>
    ),
    asset.onDepositClick && (
      <DropdownMenuItem
        key="deposit"
        LeadingIcon={<Icon name="CoinArrowInCustom" />}
        onClick={asset.onDepositClick}
      >
        {t('earn.deposit')}
      </DropdownMenuItem>
    ),
  ].filter((menuItem): menuItem is React.ReactElement => Boolean(menuItem))

  if (menuItems.length === 0) return []

  return [
    <DropdownMenu
      key={`actions-${asset.currency}`}
      DropdownMenuItems={menuItems}
      testId={`earn-asset-actions-${asset.currency}`}
    >
      <Button variant="ghost" iconElement={<Icon name="DotsThreeVerticalCustom" />} />
    </DropdownMenu>,
  ]
}

export const EarnMyAssets = ({ assets, showCustomerEarnRate = true }: EarnMyAssetsProps) => {
  const { t } = useTranslation()

  const columns: DataTableHeaderProps[] = useMemo(() => {
    const allColumns: (DataTableHeaderProps | false)[] = [
      { key: 'asset', label: t('earn.assetColumn'), variant: DataTableCellVariants.TEXT },
      {
        key: 'productName',
        label: t('earn.productNameColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'myEarnRate',
        label: showCustomerEarnRate ? t('earn.myEarnRateColumn') : t('earn.rateColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      showCustomerEarnRate && {
        key: 'customerEarnRate',
        label: t('earn.customerEarnRateColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'availableFunds',
        label: t('earn.availableFundsColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      { key: 'actions', label: '', variant: DataTableCellVariants.ACTION },
    ]

    return allColumns.filter((column): column is DataTableHeaderProps => Boolean(column))
  }, [showCustomerEarnRate, t])

  const tableData: DataTableRowData[] = useMemo(
    () =>
      assets.map(asset => {
        const cells: (DataTableCellData | false)[] = [
          {
            key: 'asset',
            variant: DataTableCellVariants.TEXT,
            Avatar: (
              <Avatar type="currency" name={getAssetAvatarCurrency(asset.currency)} size="small" />
            ),
            Text: asset.currency,
          },
          asset.comingSoon
            ? {
                key: 'productName',
                variant: DataTableCellVariants.HIGHLIGHT,
                Highlights: [
                  <Highlight
                    key="coming-soon"
                    type={HighlightType.Feature}
                    variant={HighlightVariant.Emphasis}
                  >
                    {t('earn.comingSoon').toUpperCase()}
                  </Highlight>,
                ],
              }
            : {
                key: 'productName',
                variant: DataTableCellVariants.TEXT,
                Text: asset.productName,
              },
          {
            key: 'myEarnRate',
            variant: DataTableCellVariants.TEXT,
            Text: asset.myEarnRate ?? '',
          },
          showCustomerEarnRate && {
            key: 'customerEarnRate',
            variant: DataTableCellVariants.TEXT,
            Text: asset.customerEarnRate ?? '',
          },
          {
            key: 'availableFunds',
            variant: DataTableCellVariants.TEXT,
            Text: asset.availableFundsAmount ?? '',
            BottomText: asset.availableFundsLabel,
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            align: DataTableColumnAlignments.RIGHT,
            Actions: buildAssetActionsMenu(asset, t),
          },
        ]

        return {
          id: asset.currency,
          cells: cells.filter((cell): cell is DataTableCellData => Boolean(cell)),
        }
      }),
    [assets, showCustomerEarnRate, t]
  )

  return (
    <Box flexCol gap={16} testId="earn-my-assets">
      <p className="text-h4 text-foreground-prominent">{t('earn.myAssetsTitle')}</p>

      <DataTable headers={columns} data={tableData} />
    </Box>
  )
}
